import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { buildClientesDeepLink } from "@/lib/clientesDeepLink";

const LIGHT_RANK = { red: 0, yellow: 1, green: 2, gray: 3 };

const STYLE = {
  red: {
    wrap: "border-rose-300 bg-rose-50 text-rose-950",
    badge: "bg-rose-600 text-white",
    label: "Urgente",
  },
  yellow: {
    wrap: "border-amber-300 bg-amber-50 text-amber-950",
    badge: "bg-amber-600 text-white",
    label: "Por vencer",
  },
};

/**
 * Banner global: alertas FIEL empresa + representante (GET /api/alerts/certificates).
 * Auxiliares: solo clientes asignados. Umbrales: 30 días (amarillo) / 15 días (rojo).
 *
 * @param {{ className?: string, compact?: boolean }} props
 */
export default function CertificateExpirationBanner({ className = "", compact = false }) {
  const [alerts, setAlerts] = useState(null);
  const [scoped, setScoped] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/alerts/certificates", { timeout: 20000 })
      .then(({ data }) => {
        if (!cancelled) {
          const items = Array.isArray(data?.items) ? data.items : [];
          setAlerts(items.filter((a) => a.traffic_light === "red" || a.traffic_light === "yellow"));
          setScoped(Boolean(data?.scoped_to_assigned_clients));
        }
      })
      .catch(() => {
        if (!cancelled) setAlerts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const urgentList = useMemo(() => {
    if (!alerts?.length) return [];
    return [...alerts].sort(
      (a, b) => (LIGHT_RANK[a.traffic_light] ?? 9) - (LIGHT_RANK[b.traffic_light] ?? 9),
    );
  }, [alerts]);

  const urgent = urgentList[0] || null;

  if (loading || !urgent) {
    return null;
  }

  const style = STYLE[urgent.traffic_light] || STYLE.yellow;
  const more = urgentList.length > 1 ? urgentList.length - 1 : 0;
  const credLink = buildClientesDeepLink({ clientId: urgent.client_id, tab: "credenciales" });
  const wrapClass = compact
    ? `rounded-sm border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${style.wrap} ${className}`
    : `mx-4 sm:mx-6 lg:mx-8 mt-3 rounded-sm border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${style.wrap} ${className}`;

  const extraLines = compact ? urgentList.slice(0, 3) : urgentList.slice(0, 2);

  return (
    <div className={wrapClass} role="alert" data-testid="certificate-expiration-banner">
      <div className="flex items-start gap-3 min-w-0">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm ${style.badge}`}
            >
              {style.label}
            </span>
            <span className="text-sm font-semibold">Certificados FIEL</span>
            {scoped ? (
              <span className="text-[10px] uppercase tracking-wide text-slate-600">· Mis clientes</span>
            ) : null}
          </div>
          {extraLines.map((row) => (
            <p key={`${row.client_id}-${row.cert_type}`} className="text-xs leading-relaxed">
              <strong>{row.client_name || row.client_rfc}</strong>
              {" — "}
              <span className="font-medium">{row.cert_type}:</span> {row.message}
            </p>
          ))}
          {more > 0 && extraLines.length <= 1 ? (
            <p className="text-[11px] text-slate-700">
              +{more} alerta{more > 1 ? "s" : ""} adicional{more > 1 ? "es" : ""} en el despacho
            </p>
          ) : null}
          {more > 0 && extraLines.length > 1 ? (
            <p className="text-[11px] text-slate-700">
              +{Math.max(0, urgentList.length - extraLines.length)} alerta
              {urgentList.length - extraLines.length !== 1 ? "s" : ""} más
            </p>
          ) : null}
        </div>
      </div>
      <Link
        to={credLink}
        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide shrink-0 underline underline-offset-2"
        data-testid="certificate-expiration-banner-link"
      >
        Ir a certificados
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

/** Mapa client_id → peor alerta activa (amarillo/rojo). */
export function useCertificateAlertsMap() {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/alerts/certificates", { timeout: 20000 })
      .then(({ data }) => {
        if (cancelled) return;
        const items = (Array.isArray(data?.items) ? data.items : []).filter(
          (a) => a.traffic_light === "red" || a.traffic_light === "yellow",
        );
        const rank = { red: 0, yellow: 1 };
        const next = {};
        for (const row of items) {
          const cid = row.client_id;
          if (!cid) continue;
          const prev = next[cid];
          if (!prev || (rank[row.traffic_light] ?? 9) < (rank[prev.traffic_light] ?? 9)) {
            next[cid] = row;
          }
        }
        setMap(next);
      })
      .catch(() => {
        if (!cancelled) setMap({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { alertsByClientId: map, loading };
}
